import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { LanguageSwitcher, useT } from '@insurensa/i18n';
import { useAuth } from '../lib/auth.js';
import { ApiError } from '../lib/api.js';
import { ErrorAlert } from '../components/ui.js';

/**
 * Operator sign-in.
 *
 * Uses the shared auth layout (`theme/insurensa-auth.css`) rather than the
 * theme's `.modal-dialog`, which is a modal primitive: it clamps to 500px above
 * 576px and sets `pointer-events: none`.
 */
export function LoginPage(): JSX.Element {
  const { admin, loading, signIn } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="auth-page auth-page--center" />;
  if (admin) return <Navigate to="/" replace />;

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(username, password);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  };

  const apiError = error instanceof ApiError ? error : null;

  return (
    <div className="auth-page auth-page--center">
      <div className="auth-container">
        <div className="auth-brand">
          <img src="/theme/assets/images/logo.png" alt="Insurensa" />
        </div>

        <div className="auth-language">
          <LanguageSwitcher />
        </div>

        <form className="auth-card" onSubmit={onSubmit}>
          <div className="auth-card__header">
            <h1 className="auth-card__title">{t('admin.login.title')}</h1>
            <p className="auth-card__subtitle">{t('admin.login.subtitle')}</p>
          </div>

          <div className="auth-card__body">
            <ErrorAlert error={error} />

            <div className="form-group">
              <label htmlFor="username">{t('fields.username')}</label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                className="form-control"
                placeholder={t('admin.login.usernamePlaceholder')}
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                aria-invalid={apiError?.fieldError('username') ? 'true' : undefined}
              />
              {apiError?.fieldError('username') && (
                <div className="field-error">{apiError.fieldError('username')}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('fields.password')}</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="form-control"
                placeholder={t('admin.login.passwordPlaceholder')}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block"
              disabled={submitting}
              style={{ backgroundColor: '#5CBC34', borderColor: '#5CBC34' }}
            >
              {submitting ? t('admin.login.submitting') : t('admin.login.submit')}
            </button>
          </div>
        </form>

        <div className="auth-legal">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </div>
      </div>
    </div>
  );
}
